import { reactive, readonly, provide, inject } from 'vue'
import { cloneDeep } from 'lodash'

import { orderInit } from './modules/order/index'

/* --------------- 纯 reducer --------------- */
function reducer(state, action) {
  switch (action.type) {
    case 'STEP_CHANGE': {
      const { index } = action.payload
      const visited = state.visitedSteps.includes(index)
        ? state.visitedSteps
        : [...state.visitedSteps, index].sort((a, b) => a - b)
      return { ...state, currentStep: index, visitedSteps: visited }
    }

    case 'FIELD_CHANGE': {
      const { stepKey, fieldKey, value } = action.payload

      return {
        ...state,
        data: {
          ...state.data,
          [stepKey]: { ...state.data[stepKey], [fieldKey]: value },
        },
      }
    }

    case 'BATCH_UPDATE_FIELD': {
      const { updates } = action.payload
      const newData = { ...state.data }

      // 遍历传入的更新对象
      Object.keys(updates).forEach((key) => {
        if (newData[key]) {
          newData[key] = { ...newData[key], ...updates[key] }
        }
      })

      return {
        ...state,
        data: newData,
      }
    }

    case 'RESET_ALL_DATA': {
      // 深拷贝，避免各模块间及 reset 前后共享同一份 data 引用
      return cloneDeep(orderInit)
    }

    default:
      return state
  }
}

/* --------------- 模块工厂：一份 miniStore 内按场景值缓存多个模块 --------------- */
function createModule(sceneKey) {
  const _state = reactive(cloneDeep(orderInit))

  return {
    sceneKey,
    // 只读视图
    state: readonly(_state),

    // 唯一写入口
    dispatch(action) {
      const nextState = reducer(_state, action)
      Object.keys(nextState).forEach((k) => {
        _state[k] = nextState[k] // 保持响应式
      })
    },
  }
}

// 业务场景值（新增业务场景在此扩展）
export const MINI_MODULE = {
  SIGN: 'sign', // 签约单
  RENEW: 'renew', // 续约单
  ORDER_DETAILS: 'orderDetails', // 订单详情页扫码弹窗（命令式挂载兜底）
}

const moduleMap = new Map()

/* --------------- 一份 miniStore 实例：按场景值取模块，复用不新建 --------------- */
export const miniStore = {
  getModule(sceneKey) {
    if (!moduleMap.has(sceneKey)) {
      moduleMap.set(sceneKey, createModule(sceneKey))
    }
    return moduleMap.get(sceneKey)
  },
}

/* --------------- provide / inject：页面注入场景值，组件树内共享 --------------- */
const MINI_STORE_KEY = Symbol('miniStore')

export function provideMiniStore(sceneKey) {
  provide(MINI_STORE_KEY, sceneKey)
}

// 统一按场景值取模块：显式传场景值（命令式挂载/异步回调），否则回退到 inject（组件树内）
export function useMiniStore(sceneKey) {
  const key = sceneKey || inject(MINI_STORE_KEY, null)
  if (!key) {
    throw new Error(
      '[miniStore] useMiniStore() 未找到 provider：请通过 provideMiniStore(sceneKey) 提供，或显式传入场景值（MINI_MODULE.xxx）',
    )
  }
  return miniStore.getModule(key)
}
